import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  completeVoucherPurchase,
  clearVoucherErrors,
} from '../../../actions/action_vouchers';
import VoucherErrorBoundary from './VoucherErrorBoundary';

const PageWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 16px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: #22c55e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 32px 0;
`;

const VoucherCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  color: white;
  margin-bottom: 32px;
`;

const VoucherValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const VoucherValueLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

const VoucherProductName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  opacity: 0.9;
`;

const BalanceBreakdown = styled.div`
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  text-align: left;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 18px;
    padding-top: 16px;
    border-top: 2px solid #e5e7eb;
    margin-top: 4px;
  }
`;

const BalanceLabel = styled.span`
  color: #666;
`;

const BalanceAmount = styled.span`
  color: #22c55e;
  font-weight: 600;
`;

const InfoBox = styled.div`
  background-color: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: left;
  font-size: 14px;
  color: #1e40af;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;

  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const PrimaryButton = styled.button`
  padding: 14px 32px;
  background-color: #22c55e;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #16a34a;
  }
`;

const SecondaryButton = styled.button`
  padding: 14px 32px;
  background-color: white;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9ca3af;
    background-color: #f9fafb;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: #666;
  margin-top: 16px;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 20px;
  color: #dc2626;
  margin-bottom: 24px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background-color: #ef4444;
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 12px;

  &:hover {
    background-color: #dc2626;
  }
`;

function VoucherPurchaseSuccess({ history }) {
  const dispatch = useDispatch();
  const [sessionId, setSessionId] = useState(null);

  const completionLoading = useSelector(
    (state) => state.vouchers.completionLoading,
  );
  const completionError = useSelector(
    (state) => state.vouchers.completionError,
  );
  const purchasedVoucher = useSelector(
    (state) => state.vouchers.purchasedVoucher,
  );

  useEffect(() => {
    // Get session_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get('session_id');

    if (urlSessionId) {
      setSessionId(urlSessionId);
      dispatch(completeVoucherPurchase(urlSessionId));
    }
  }, [dispatch]);

  const handleRetry = () => {
    if (sessionId) {
      dispatch(clearVoucherErrors());
      dispatch(completeVoucherPurchase(sessionId));
    }
  };

  const handleGoToDashboard = () => {
    history.push('/dashboard');
  };

  const handleBuyAnother = () => {
    history.push('/vouchers/purchase');
  };

  const renderLoading = () => (
    <LoadingWrapper>
      <div className="spinner" />
      <LoadingText>Processing your purchase...</LoadingText>
    </LoadingWrapper>
  );

  const renderError = () => (
    <>
      <ErrorMessage>
        <h3 style={{ margin: '0 0 8px 0' }}>Something went wrong</h3>
        <p style={{ margin: 0 }}>{completionError}</p>
        <RetryButton onClick={handleRetry}>Try Again</RetryButton>
      </ErrorMessage>
      <SecondaryButton onClick={handleGoToDashboard}>
        Return to Dashboard
      </SecondaryButton>
    </>
  );

  const renderSuccess = () => {
    if (!purchasedVoucher) {
      return null;
    }

    return (
      <>
        <SuccessIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </SuccessIcon>

        <Title>Purchase Complete!</Title>
        <Subtitle>
          Your voucher is ready to use. Check your email for your voucher code
          and receipt.
        </Subtitle>

        <VoucherCard>
          <VoucherProductName>
            {purchasedVoucher.product_name}
          </VoucherProductName>
          <VoucherValue>
            {purchasedVoucher.total_balance_formatted}
          </VoucherValue>
          <VoucherValueLabel>Total Credit Available</VoucherValueLabel>
        </VoucherCard>

        <BalanceBreakdown>
          <BalanceRow>
            <BalanceLabel>Purchased Credit</BalanceLabel>
            <BalanceAmount>
              {purchasedVoucher.purchased_balance_formatted}
            </BalanceAmount>
          </BalanceRow>
          {purchasedVoucher.promo_balance_formatted &&
            purchasedVoucher.promo_balance_formatted !== '$0.00' && (
              <BalanceRow>
                <BalanceLabel>Bonus Promo Credit</BalanceLabel>
                <BalanceAmount>
                  {purchasedVoucher.promo_balance_formatted}
                </BalanceAmount>
              </BalanceRow>
            )}
          <BalanceRow>
            <BalanceLabel>Total to Spend</BalanceLabel>
            <BalanceAmount>
              {purchasedVoucher.total_balance_formatted}
            </BalanceAmount>
          </BalanceRow>
        </BalanceBreakdown>

        <InfoBox>
          <strong>How to use your credit:</strong> At checkout, your voucher
          balance will be automatically available. Simply check the box to apply
          your credit to your order.
        </InfoBox>

        {purchasedVoucher.promo_expires_at &&
          !purchasedVoucher.promo_expired && (
            <InfoBox
              style={{
                backgroundColor: '#fef3c7',
                borderColor: '#f59e0b',
                color: '#92400e',
              }}
            >
              <strong>Note:</strong> Your bonus credit expires on{' '}
              {new Date(purchasedVoucher.promo_expires_at).toLocaleDateString(
                'en-US',
                {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                },
              )}
              . Purchased credit never expires.
            </InfoBox>
          )}

        <ActionButtons>
          <PrimaryButton onClick={handleGoToDashboard}>
            Start Shopping
          </PrimaryButton>
          {/* TODO: Re-enable when voucher purchase flow is ready for direct purchase
          <SecondaryButton onClick={handleBuyAnother}>
            Buy Another Voucher
          </SecondaryButton>
          */}
        </ActionButtons>
      </>
    );
  };

  // No session ID in URL
  if (!sessionId) {
    return (
      <PageWrapper>
        <ErrorMessage>
          <h3 style={{ margin: '0 0 8px 0' }}>Invalid Request</h3>
          <p style={{ margin: 0 }}>
            No purchase session found. Please try purchasing again.
          </p>
        </ErrorMessage>
        {/* TODO: Re-enable when voucher purchase flow is ready for direct purchase
        <SecondaryButton onClick={handleBuyAnother}>
          Go to Voucher Purchase
        </SecondaryButton>
        */}
        <SecondaryButton onClick={handleGoToDashboard}>
          Go to Dashboard
        </SecondaryButton>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {completionLoading && renderLoading()}
      {completionError && !completionLoading && renderError()}
      {purchasedVoucher &&
        !completionLoading &&
        !completionError &&
        renderSuccess()}
    </PageWrapper>
  );
}

VoucherPurchaseSuccess.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

// Wrap with error boundary to catch JavaScript errors and display a user-friendly fallback
function VoucherPurchaseSuccessWithErrorBoundary(props) {
  return (
    <VoucherErrorBoundary>
      <VoucherPurchaseSuccess {...props} />
    </VoucherErrorBoundary>
  );
}

VoucherPurchaseSuccessWithErrorBoundary.propTypes =
  VoucherPurchaseSuccess.propTypes;

export default VoucherPurchaseSuccessWithErrorBoundary;
